<template>
  <v-container>

    <loading v-model="loading" />

    <v-snackbar v-model="msg.show" value="true" right top multi-line :color="msg.type" :timeout="msg.time">
      {{ msg.text }}
      <v-btn dark flat @click="msg.show = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-card fluid>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>{{ $t('Delivery notes') }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <div v-for="header in headersWithFilters" :key="header.text" class="mx-1">
          <template v-if="header.value === 'date'">
            <v-input-date-range-picker v-model="header.filter" :label="header.text" @input="getItems"></v-input-date-range-picker>
          </template>
          <template v-else-if="header.value === 'status_id'">
            <v-select
              :label="header.text"
              v-model="header.filter"
              :items="STATUSES"
              item-value="id" item-text="name"
              @change="getItems()"

              clearable
              @click:clear="header.filter = null; getItems()"

              hide-details
              full-width background-color="grey lighten-4"
            >
              <template v-slot:selection="{ item }">
                <v-chip :color="item.color" dark label small>
                  {{ $t('WMS.STATUS.' + item.name) }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <v-chip :color="item.color" dark label>
                  {{ $t('WMS.STATUS.' + item.name) }}
                </v-chip>
              </template>
            </v-select>
          </template>
          <template v-else>
            <v-text-field
              :label="header.text"
              v-model="header.filter"
              @keyup.enter="getItems()"

              append-icon="search"
              @click:append="getItems()"

              clearable
              @click:clear="header.filter = null; getItems()"

              hide-details
              full-width background-color="grey lighten-4"
            ></v-text-field>
          </template>
        </div>

        <v-spacer class="mx-1"></v-spacer>

        <v-tooltip bottom>
         <v-btn icon slot="activator" @click="exportExcel" v-shortkey="['ctrl', 'alt', 'e']" @shortkey="exportExcel">
          <v-icon class="text--secondary">fa-file-excel</v-icon>
         </v-btn>
         <span>{{ $t('Export to Excel') }}</span>
        </v-tooltip>

        <v-divider vertical inset></v-divider>

        <v-tooltip bottom><!-- TODO: extract to component -->
          <v-menu slot="activator"
            :close-on-content-click="false"
            :nudge-width="200"
            offset-x
            left
          >
            <v-btn icon slot="activator">
              <v-icon v-if="headers.some(header => !header.show)" class="text--secondary">settings_suggest</v-icon>
              <v-icon v-else class="text--secondary">settings</v-icon>
            </v-btn>

            <v-card>
              <v-list>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('Table settings') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ $t('Select visible columns') }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
              <v-divider></v-divider>
              <v-list dense>
                <v-list-tile v-for="(header,key) in headers.filter(header => header.value !== 'actions')" :key="'col-'+key" @click.prevent="columnToggle(header)">
                  <v-list-tile-action>
                    <v-switch :value="header.show" :input-value="header.show" color="primary" :disabled="columnHideCheck(header)"></v-switch>
                  </v-list-tile-action>
                  <v-list-tile-title>{{ header.text }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-card>
          </v-menu>
          <span>{{ $t('Table settings') }}</span>
        </v-tooltip>
      </v-toolbar>

      <v-data-table
        :headers="headersShown"
        :items="items"
        :pagination.sync="pagination"
        :total-items="totalItems"
        item-key="order_code"
        :loading="loading"
        :rows-per-page-items="[5, 10, 25, 50]"
        :rows-per-page-text="$t('Rows per page')"
        :no-data-text="$t('No data')"
        :no-results-text="$t('No results')"
        class="elevation-1"
      >
        <template v-slot:progress>
          <v-progress-linear indeterminate height="2" style="position: absolute; width: 100%; z-index: 10;"></v-progress-linear>
        </template>
        <template v-slot:items="props">
          <tr
            :active="props.expanded"
            @click="props.item.detail.length ? props.expanded = !props.expanded : null; isCompletedStatus(props.item.status_id) && props.expanded ? getPickedProducts(props.item.order_code) : clearPicked()"
            :style="props.item.detail.length === 0 ? 'opacity: 0.3' : ''"
            :class="props.item.is_shipment ? 'green lighten-5' : ''"
          >
            <td
              v-for="header in headersShown"
              :key="header.value"
              :class="['text-xs-' + header.align, header.value === 'actions' || header.value === 'manifest' ? 'px-1' : '']"
            >
              <template v-if="header.value === 'order_code'">
                <span :class="props.item.customer ? 'green--text' : ''">{{ props.item.order_code }}</span>
              </template>

              <template v-else-if="header.value === 'date'">
                {{ printDateTime(props.item.date) }}
              </template>

              <template v-else-if="header.value === 'completion_date'">
                {{ printDateTime(props.item.completion_date) }}
              </template>

              <template v-else-if="header.value === 'product_count'">
                {{ props.item.detail.length }}
              </template>

              <template v-else-if="header.value === 'status_id'">
                <v-chip label :color="getStatus(props.item.status_id).color" text-color="white">
                  <span class="font-weight-bold">{{ props.item.status_name || $t('WMS.STATUS.' + getStatus(props.item.status_id).name) || props.item.status_id }}</span>
                </v-chip>
              </template>

              <template v-else-if="header.value === 'wms_station_code'">
                {{ props.item.tp_station_name }}
              </template>

              <template v-else-if="header.value === 'address'">
                <template v-if="props.item.address">{{ props.item.address }}</template>
                <template v-else-if="props.item.customer">
                  <v-icon small class="mr-2">add</v-icon>
                  <span class="grey--text text--darken-1 font-weight-light">{{ addressString(props.item.customer.address).address }}</span>
                </template>
              </template>

              <template v-else-if="header.value === 'manifest'">
                <template v-if="props.item.manifest">
                  <v-tooltip bottom v-if="props.item.state">
                    <v-btn slot="activator" flat small class="grey lighten-3 grey--text"
                      :icon="!$vuetify.breakpoint.lgAndUp"
                      :round="$vuetify.breakpoint.lgAndUp"
                    >
                      <v-icon small>{{ getStateIcon(props.item.state) }}</v-icon>
                      <span v-if="$vuetify.breakpoint.lgAndUp" class="ml-2">{{ props.item.state.display || props.item.state.name }}</span>
                    </v-btn>
                    <span>{{ props.item.state.display || props.item.state.name }}</span>
                  </v-tooltip>

                  <v-chip color="info" outline>
                    <v-tooltip bottom>
                      <v-avatar slot="activator">
                        <v-icon @click.stop="shipmentTrack(props.item)">search</v-icon>
                      </v-avatar>
                      <span>{{ $t('Track shipment') }} ({{ $t('In a new tab or window') }})</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <span slot="activator" @click.stop="shipmentEdit(props.item)" class="cursor-link">{{ props.item.manifest }}</span>
                      <v-icon small color="white" left>edit</v-icon><span>{{ $t('Edit shipment') }}</span>
                    </v-tooltip>
                  </v-chip>
                </template>

                <v-tooltip bottom v-else-if="props.item.is_shipment">
                  <v-btn slot="activator" icon flat small @click.stop="shipmentCreate(props.item)" class="info--text">
                    <v-icon>{{ props.item.customer ? 'add_circle' : 'add_circle_outline' }}</v-icon>
                  </v-btn>
                  <span>{{ $t(props.item.customer ? 'Create shipment' : 'Create shipment (set receiver manualy)') }}</span>
                </v-tooltip>
              </template>

              <div v-else-if="header.value === 'actions'" class="text-no-wrap">
                <v-tooltip bottom v-if="props.item.status_id === 1">
                  <v-btn slot="activator" icon flat @click.stop="cancel(props.item.order_code)" color="error" class="ma-1">
                    <v-icon>cancel</v-icon>
                  </v-btn>
                  <span>{{ $t('Cancel delivery note') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-btn icon slot="activator" @click="exportExcelOrder(props.item)" v-shortkey="['ctrl', 'alt', 'e']" @shortkey="exportExcelOrder(props.item)" class="ma-1">
                    <v-icon class="text--secondary">fa-file-excel</v-icon>
                  </v-btn>
                  <span>{{ $t('Export to Excel') }}</span>
                </v-tooltip>
              </div>

              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
          </tr>
        </template>
        <template v-slot:expand="props">

          <v-card flat class="grey lighten-4" style="border-top: solid white 1px !important">

            <v-layout fill-height align-center>
              <v-flex class="xs12 sm8 py-2 pr-4" style="border-right: solid white 1px">
                <template v-if="!isCompletedStatus(props.item.status_id)">
                <v-layout v-for="(detail, index) in props.item.detail" :key="detail.product_code" class="py-1">
                  <v-flex class="xs1 pr-4 text-xs-right">
                    <span class="grey--text text--darken-1 font-weight-light">{{ index+1 }}</span>
                  </v-flex>
                  <v-flex>
                    <span class="grey--text text--darken-1 font-weight-light mr-1">{{ detail.product_code }}</span>
                    {{ detail.product_name }}
                  </v-flex>
                  <v-flex class="xs2 text-xs-right" v-if="!!detail.lot">
                    <span class="grey--text text--darken-1 font-weight-light">{{ $t('Lot') }}</span><br/>
                    {{ detail.lot }}
                  </v-flex>
                  <v-flex class="xs2 text-xs-right" v-if="!!detail.exp_date">
                    <span class="grey--text text--darken-1 font-weight-light">{{ $t('Expiry date') }}</span><br/>
                    {{ printDateTime(detail.exp_date, 1) }}
                  </v-flex>
                  <v-flex class="xs2 text-xs-right">
                    {{ detail.weight }}
                    <span class="grey--text text--darken-1 font-weight-light ml-1">{{ $t('kg') }}</span>
                  </v-flex>
                  <v-flex class="xs1 text-xs-right">
                    {{ detail.quantity }}
                    <span class="grey--text text--darken-1 font-weight-light ml-1">{{ detail.unit_code }}</span>
                  </v-flex>
                </v-layout>
              </template>
              <template v-else-if="props.expanded">
                <v-layout v-for="(detail, index) in picked" :key="detail.product_code" class="py-1">
                  <v-flex class="xs1 pr-4 text-xs-right">
                    <span class="grey--text text--darken-1 font-weight-light">{{ index+1 }}</span>
                  </v-flex>
                  <v-flex>
                    <span class="grey--text text--darken-1 font-weight-light mr-1">{{ detail.product_code }}</span>
                    {{ detail.product_name }}
                  </v-flex>
                  <v-flex class="xs2 text-xs-right" v-if="!!detail.lot">
                    <span class="grey--text text--darken-1 font-weight-light">{{ $t('Lot') }}</span><br/>
                    {{ detail.lot }}
                  </v-flex>
                  <v-flex class="xs2 text-xs-right" v-if="!!detail.exp_date">
                    <span class="grey--text text--darken-1 font-weight-light">{{ $t('Expiry date') }}</span><br/>
                    {{ printDateTime(detail.exp_date, 1) }}
                  </v-flex>
                  <v-flex class="xs1 text-xs-right">
                    {{ detail.quantity }}
                    <span class="grey--text text--darken-1 font-weight-light ml-1">{{ detail.unit_code }}</span>
                  </v-flex>
                </v-layout>
              </template>
              </v-flex>
              <v-flex class="xs12 sm4 pa-2 pl-3" v-if="props.item.manifest || props.item.customer">
                <v-layout fill-height align-center v-if="props.item.manifest">
                  <v-flex shrink>
                    <v-tooltip bottom>
                      <v-icon slot="activator" class="mr-2">{{ props.item.is_office ? 'warehouse' : 'home' }}</v-icon>
                      <span>{{ $t(props.item.is_office ? 'Office' : 'Door') }}</span>
                    </v-tooltip>
                  </v-flex>
                  <v-flex class="pl-2">
                    <span class="grey--text text--darken-1 font-weight-light mr-2">{{ $t('Address') }}</span>
                    {{ props.item.address || '-' }}
                  </v-flex>
                </v-layout>
                <template v-else-if="props.item.customer">
                  <span class="grey--text text--darken-1 font-weight-light mr-2">{{ $t('Customer') }}</span>
                  <span class="mr-2">{{ props.item.customer.partner.name }}</span>
                  <span class="grey--text mr-2">&bull;</span>
                  <span class="grey--text text--darken-1 font-weight-light mr-2">{{ props.item.customer.contact_person }}</span>
                  <span class="grey--text mr-2" v-if="props.item.customer.contact_person">&bull;</span>
                  {{ addressString(props.item.customer.address).address }}
                </template>
              </v-flex>
            </v-layout>

          </v-card>

        </template>

        <template v-slot:footer>
          <td :colspan="headersShown.length" class="text-xs-right">

            {{ $t('Delivery notes total') }}:
            <span class="font-weight-bold ml-2">{{ totalItems }}</span>

          </td>
        </template>
        <template v-slot:actions-prepend>
          <v-pagination v-model="pagination.page" circle :length="pages" total-visible="7" class="pagination"></v-pagination>
        </template>
      </v-data-table>

    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions }                     from 'vuex';
import { wms, shipments }                             from '@/resources';
import { getStateIcon, addressString, printDateTime, downloadFile } from '@/components/util';
import Loading                                        from '@/components/Loading.vue';
import VInputDateRangePicker                          from '@/components/VInputDateRangePicker';

const ERROR_POPUP_TIMEOUT = 30000;
const STATUSES = [
  {id:  1,  color: 'warning',       name: 'NEW'},// Нова
  {id:  2,  color: 'info',          name: 'IN_PROGRESS'},// В изпълнение
  {id:  9,  color: 'success',       name: 'COMPLETED'},// Завършена
  {id:  5,  color: 'orange',        name: 'LOCKED'},// Заключена
  {id: 10,  color: 'lime darken-1', name: 'CHECKED'},// Подготвена
  {id: 11,  color: 'success',       name: 'LOADED'},// Експедирана
  {id: 98,  color: 'error',         name: 'CANCELED'},// Анулирана
];
const COMPLETED_STATUSES = [
  {id:  9,  name: 'COMPLETED'},
  {id: 10,  name: 'CHECKED'},// Подготвена
  {id: 11,  name: 'LOADED'},// Експедирана
];

export default {
  name: 'wms-orders',
  components: {
    Loading,
    VInputDateRangePicker
  },
  data () {
    return {
      loading: false,

      msg: {
        type: '',
        text: '',
        time: 5000,
        show: false,
        popup: (text, type) => {
          this.msg.type = type || 'info';
          this.msg.text = text;
          this.msg.time = type === 'error' ? ERROR_POPUP_TIMEOUT : 5000;
          this.msg.show = true;
        }
      },

      headers: [
        { text: this.$t('Code'),            value: 'order_code',       align: 'left',   sortable: true,  show: true,  nofilter: true },
        { text: this.$t('Order date'),      value: 'date',             align: 'left',   sortable: true,  show: true,  filter: { start_date: undefined, end_date: undefined } },
        { text: this.$t('Completion date'), value: 'completion_date',  align: 'left',   sortable: false, show: true,  nofilter: true },
        { text: this.$t('Pr. cnt'),         value: 'product_count',    align: 'right',  sortable: false, show: true,  nofilter: true },
        { text: this.$t('Status'),          value: 'status_id',        align: 'left',   sortable: true,  show: true,  filter: null, width: '10rem' },
        { text: this.$t('Warehouse'),       value: 'wms_station_code', align: 'left',   sortable: true,  show: true,  nofilter: true },
        { text: this.$t('Target Address'),  value: 'address',          align: 'left',   sortable: true,  show: false, nofilter: true },
        { text: this.$t('Shipment'),        value: 'manifest',         align: 'right',  sortable: false, show: true,  nofilter: true },
        { text: this.$t('Actions'),         value: 'actions',          align: 'right',  sortable: false, show: true,  nofilter: true, persist: true },
      ],
      items: [],
      totalItems: 0,
      pagination: {
        rowsPerPage: 10,
        sortBy: 'date',
        descending: true
      },
      states: [],
      STATUSES,
      COMPLETED_STATUSES,

      getStateIcon: obj => getStateIcon(obj).stateIcon,
      addressString,
      printDateTime,
      picked: [],
    }
  },
  computed: {
    ...mapGetters(['wmsOrdersFilter']),

    pages() {
      if (this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) return 0;

      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    },

    headersShown() {
      return this.headers.filter(elem => !!elem.show);
    },
    headersWithFilters() {
      return this.headers.filter(elem => !elem.nofilter);
    },

    filtersOn() {
      let filters = {};
      this.headers.forEach(elem => {
        if (!!elem.filter) {
          let key = elem.value;
          if (elem.value === 'date') {
            ['start_date', 'end_date'].forEach(key2 => {

              if (elem.filter[key2]) {
                filters[key2] = elem.filter[key2];
              }

            });
          }
          else {
            filters[key] = elem.filter;
          }
        }
      });
      return filters;
    }
  },
  methods: {
    ...mapActions(['updateWmsOrdersFilter']),
    test(evt) {
      console.log(evt);
    },

    getItems(isPaginate) {
      if (this.loading) {
        return;
      }
      this.loading = true;

      this.storeFilter();

      let params = this.filtersOn;
      let { sortBy, descending, page, rowsPerPage } = this.pagination;
      if (!isPaginate) {
        page = 1;
      }

      let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
      let length = (rowsPerPage>=1) ? rowsPerPage : -1;
      let sort   = (sortBy) ? (descending ? '-' : '') + sortBy : '';
      if (sort)
        params = Object.assign({ length, offset, sort }, params);
      else
        params = Object.assign({ length, offset }, params);

      wms.listOrders(params).then(response => {

        this.totalItems = response.body.length;
        this.items      = response.body.result;
        this.items.forEach(elem => {
          if (elem.shipment_id) {
            elem.manifest = elem.order_code;
          }
        });
        // MOCK
        // this.items.forEach(elem => {
        //   if (elem.manifest) {
        //     if (!elem.shipment_id) {
        //       elem.shipment_id = elem.manifest;
        //     }
        //     if (!elem.state) {
        //       elem.state = {
        //         code: 'CCCCS',
        //         name: 'ACCEPTED'
        //       };
        //     }
        //     if (!elem.address) {
        //       elem.address = '[BG-1000] София (София-град) Михаил Тенев №12, ет.1, ап.2';
        //     }
        //   }
        // });

        if (this.states.length) {
          this.matchStates();
        }
      }).catch(response => {

        if (response.body.errors) {
          this.msg.popup(response.body.errors.map(elem => elem.text).join(', '), 'error');
        }
        else {
          this.msg.popup((response.status === 403 ? this.$t('Access denied') : this.$t('Error loading orders list: ') + response.statusText), 'error');
        }

      }).finally(() => {
        this.loading = false;
      });
    },

    matchStates() {
      this.items.forEach(item => {
        if (item.state) {
          const mstate = this.states.find(state => state.code === item.state.code);
          if (mstate) {
            item.state.display = mstate.display;
          }
        }
      });
    },

    columnHideCheck(header) {
      if (header.persist)
        return true;

      if (!header.show)
        return false;

      let v = 0;
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value != 'actions' && this.headers[i].show)
          v++;

      return (v<2);
    },
    columnToggle(header) {
      if (!header.persist) {
        header.show = !header.show;
        this.storeFilter();
      }
    },

    changeSort(header) {
      if (!header.sortable)
        return;
      let column = header.value;
      if (this.pagination.sortBy === column) {
        if (this.pagination.descending)
          this.pagination.sortBy = null;
        else
          this.pagination.descending = true;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    changeSortEmited(evt, header) {
      this.changeSort(header);
    },

    storeFilter() {
      let s = {
        pagination:  this.pagination,
        headersdata: this.headers.filter(header => header.value !== 'actions')
          .map(header => {
            return {
              show: !!header.show,
              filter: header.filter
            }
          }),
      };
      this.updateWmsOrdersFilter(JSON.stringify(s));
    },

    getStatus(id) {
      const status = STATUSES.find(elem => elem.id === id);
      return status || {};
    },

    // getStateIcon(obj) {
    //   return getStateIcon(obj).stateIcon;
    // },

    cancel(orderCode) {
      if (this.loading) {
        return;
      }
      this.loading = true;

      wms.cancel({order_code: orderCode}).then(response => {

        this.msg.popup(this.$t('Delivery note canceled'));
        this.getItems();

      }).catch(response => {

        if (response.body.errors) {
          this.msg.popup(response.body.errors.map(elem => elem.text).join(', '), 'error');
        }
        else {
          this.msg.popup((response.status === 403 ? this.$t('Access denied') : response.statusText), 'error');
        }

      }).finally(() => {
        this.loading = false;
      });
    },

    shipmentTrack(item) {
      let manifest = item.manifest.replace(/\#/, '%23');
      let win = window.open(location.origin + __webpack_public_path__ + '#/track/' + manifest, '_blank');
      win.focus();
      //this.$router.push({ name: "track_id", params: { id: manifest }});
    },
    shipmentEdit(item) {
      this.$router.push({ name: "request", params: { wms_order: item, id: item.shipment_id || item.manifest }});
    },
    shipmentCreate(item) {
      if (!item.tp_station_code) {
        this.msg.popup( this.$t('Cannot create shipment: source office missing!'), 'warning');
        return;
      }
      this.$router.push({ name: "request", params: { wms_order: item, id: 0 }});
    },
    getPickedProducts(orderCode) {
      if (this.loading) {
        return;
      }
      this.clearPicked();
      this.loading = true;

      wms.getPicked({order_code: orderCode, is_Completed: true}).then(response => {
        this.picked = JSON.parse(JSON.stringify(response.body.result));

        console.log(this.picked);

      }).catch(response => {

        if (response.body.errors) {
          this.msg.popup(response.body.errors.map(elem => elem.text).join(', '), 'error');
        }
        else {
          this.msg.popup((response.status === 403 ? this.$t('Access denied') : response.statusText), 'error');
        }

      }).finally(() => {
        this.loading = false;
      });
    },
    clearPicked() {
      this.picked = [];
    },
    isCompletedStatus(statusId) {
      return COMPLETED_STATUSES.some(elem => elem.id === statusId);
    },
    exportExcel() {
      if (this.loading)
        return;

      this.loading = true;

      let params = this.filtersOn;

      wms.xlsOrders(params).then(response => {
        downloadFile(response);
      }, error => {
        this.msg.popup(error.statusText, 'error');
      }).finally(() => {
        this.loading = false;
      });
    },
    exportExcelOrder(item) {
      if (this.loading)
        return;

      this.loading = true;
      let params = {
        order_code: item.order_code,
        is_completed: this.isCompletedStatus(item.status_id)
      }
      console.log(params);

      wms.xlsOrderProducts(params).then(response => {
        downloadFile(response);
      }, error => {
        this.msg.popup(error.statusText, 'error');
      }).finally(() => {
        this.loading = false;
      });
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getItems('isPaginate');
      },
      deep: true
    },
    'calendar.popup'(val) {
      if (this.calendar.popup) {
        this.calendar.dates = [];
        const header = this.headers.find(elem => elem.value === 'date');
        if (header.filter.start_date) {
          this.calendar.dates.push( header.filter.start_date );
        }
        if (header.filter.end_date) {
          this.calendar.dates.push( header.filter.end_date );
        }
      }
    }
  },
  mounted() {
    {// reStore
      let storedFilter = null;
      try {
        storedFilter = JSON.parse(this.wmsOrdersFilter);
      } catch(e) {
        console.log('Filter cannot be parsed', e);
        storedFilter = null;
      }

      if (storedFilter) {
        storedFilter.headersdata.forEach((elem, idx) => {
          if (typeof elem.show !== 'undefined') {
            this.headers[idx].show = elem.show;
          }
          if (typeof elem.filter !== 'undefined') {
            this.headers[idx].filter = elem.filter;
          }
        });

        if (storedFilter.pagination) {
          this.pagination.page        = storedFilter.pagination.page;
          this.pagination.rowsPerPage = storedFilter.pagination.rowsPerPage;
          this.pagination.totalItems  = storedFilter.pagination.totalItems;
        }
      }
    }

    if (this.states.length === 0) {
      shipments.listStates().then(res => {
        this.states = res.body.result;
      }).catch(err => {
        console.log('Cannot load states', err);
      });
    }


    // MOCK
    // this.totalItems = RESPONSE.length;
    // this.items      = RESPONSE.result;
  }
}

// MOCK
// const RESPONSE = { "offset": 0, "length": 8, "result": [ { "order_code": "12", "date": 1686663373000, "status_id": 1, "status_name": "Нова", "manifest": "", "address": "[BG-1000] София (София-град) Михаил Тенев №12, ет.1, ап.2", "detail": [ { "product_code": "120DENREN0", "quantity": 200.0 }, { "product_code": "120DENREN1", "quantity": 200.0 }, { "product_code": "120DENREN2", "quantity": 200.0 }, { "product_code": "120DENREN3", "quantity": 200.0 }, { "product_code": "120DENREN4", "quantity": 200.0 } ] }, { "order_code": "13", "date": 1686663431000, "status_id": 1, "status_name": "Нова", "manifest": "kp123", "address": null, "detail": [] }, { "order_code": "14", "date": 1686663461000, "status_id": 1, "status_name": "Нова", "manifest": "", "address": null, "detail": [] }, { "order_code": "15", "date": 1686848270000, "status_id": 1, "status_name": "Нова", "manifest": "", "address": null, "detail": [ { "product_code": "120DENREN0", "quantity": 150.0 } ] }, { "order_code": "20", "date": 1686840367000, "status_id": 1, "status_name": "Нова", "manifest": "KP567386", "address": null, "detail": [ { "product_code": "120DENREN0", "quantity": 200.0 }, { "product_code": "120DENREN1", "quantity": 200.0 } ] }, { "order_code": "21", "date": 1686841127000, "status_id": 1, "status_name": "Нова", "manifest": "12as", "address": null, "detail": [] }, { "order_code": "21 -Annulated 2023-06-15 17:58:47", "date": 1686840903000, "status_id": 98, "status_name": null, "manifest": "", "address": null, "detail": [] }, { "order_code": "KP567386", "date": 1686663745000, "status_id": 1, "status_name": "Нова", "manifest": "KP567386", "address": null, "detail": [] } ] };

</script>

<style scoped>
.input-right >>> input {
  text-align: right;
}
.pagination >>> button {
  box-shadow: none;
}

.expanded-card {
  border-top: 1px solid white;
}

.cursor-link {
  cursor: pointer;
}

.col-status_id {
  width: 10rem;
}
</style>